/********************************************************************************
 * Copyright (c) 2026 CrossBreeze, EclipseSource and others.
 *
 * This program and the accompanying materials are made available under the
 * terms of the MIT License which is available in the project root.
 *
 * SPDX-License-Identifier: MIT
 ********************************************************************************/

import { ResponseError } from 'vscode-jsonrpc';

/**
 * The framework externalizes user-facing strings and SELECTS no locale: it
 * relays the one its client declared and renders with whatever templates the
 * adopter installed, defaulting to its English. Every such string carries a
 * stable code beside that English, and exactly one side renders it — the side
 * that knows the reading user's language.
 *
 * Which side that is depends on the message, not on the package. A server
 * message is rendered by the server, at the one seam every carrier passes
 * through, in the locale it was handed at init. A message the client tier raises
 * is rendered there, because those fire when the server is unreachable. Nothing
 * is rendered twice: two renders of one sentence are two authorities over it,
 * and they diverge on the first reword.
 *
 * Codes are `hydranium/<unscoped-package>/<name>`. The package segment locates
 * the declaration, so a message is declared in the package that raises it and a
 * code never names a package it does not live in. `.` and `:` are forbidden in a
 * segment: they are i18next's default key and namespace separators, where either
 * silently becomes a nested lookup that misses.
 */
export type MessageParams = Readonly<Record<string, string | number>>;

type Placeholder<S extends string> = S extends `${string}{${infer Name}}${infer Rest}` ? Name | Placeholder<Rest> : never;

export type ParamsOf<S extends string> = [Placeholder<S>] extends [never]
   ? Record<never, never>
   : Readonly<Record<Placeholder<S>, string | number>>;

/** Required exactly when the text has placeholders, absent when it does not. */
export type ParamsArg<S extends string> = [Placeholder<S>] extends [never] ? [] : [params: ParamsOf<S>];

/**
 * Rejects a text argument already widened to `string`. Load-bearing rather than
 * defensive: the whole compile-time guarantee is conditional on `S` inferring a
 * literal, and for a concatenated or pre-widened text the placeholder set
 * silently becomes empty, `format()` accepts no arguments, and the missing
 * substitution surfaces only at runtime.
 */
export type LiteralText<S extends string> = string extends S ? never : S;

export interface MessageDefinition<S extends string> {
   readonly code: string;
   readonly text: S;
   format(...args: ParamsArg<S>): string;
}

/**
 * Declare a message. Placeholder names are inferred from `text` rather than
 * declared again in a type argument: a second spelling of every name is the
 * repetition that drifts, since adding a placeholder to the sentence and not to
 * the type compiles.
 */
export function defineMessage<S extends string>(code: string, text: LiteralText<S>): MessageDefinition<S> {
   const literal = text as S;
   return { code, text: literal, format: (...args) => interpolate(literal, args[0] ?? {}) };
}

const PLACEHOLDER = /\{([^}]+)\}/g;

/**
 * Substitute `{name}` tokens, leaving an unfilled token in place.
 *
 * It must not throw. This runs over an adopter's translation as well as our own
 * text, so a typo in a foreign catalogue has to degrade to a slightly wrong
 * sentence rather than raise inside a toast render. Re-scanning the result to
 * detect an unfilled token is what an earlier form did, and it cannot work:
 * `String.replace` does not rescan replacement text, so the check could not tell
 * an unfilled placeholder from user data shaped like one — an element literally
 * named `{separator}` crashed at the authoring site.
 */
export function interpolate(template: string, params: MessageParams): string {
   // Indexed rather than `key in params`: a hand-built or version-skewed
   // identity can arrive with no params at all, and `in` throws on a non-object
   // where a lookup degrades.
   const lookup = params as Record<string, string | number | undefined> | undefined;
   return template.replace(PLACEHOLDER, (match, key: string) => {
      const value = lookup?.[key];
      return value === undefined ? match : String(value);
   });
}

export interface MessageIdentity {
   readonly code: string;
   readonly params: MessageParams;
}

/**
 * Envelope for a protocol `data` field. Namespaced under one key so it co-exists
 * with a carrier's own `data` conventions rather than occupying `data` itself.
 */
export interface HydraniumMessageData {
   readonly hydranium: MessageIdentity;
}

/** An identity plus its resolved English — everything a renderer needs, on any carrier. */
export interface ResolvedMessage extends MessageIdentity {
   readonly text: string;
}

export function messageData<S extends string>(message: MessageDefinition<S>, ...args: ParamsArg<S>): HydraniumMessageData {
   return { hydranium: { code: message.code, params: args[0] ?? {} } };
}

/**
 * Identity plus resolved English, for a hand-off carrying a value rather than a
 * protocol field. The result is structured-clone safe, so it survives a process
 * hop where one intervenes and costs nothing where none does.
 */
export function resolve<S extends string>(message: MessageDefinition<S>, ...args: ParamsArg<S>): ResolvedMessage {
   return { code: message.code, text: message.format(...args), params: args[0] ?? {} };
}

/**
 * Validates every field {@link MessageIdentity} declares, `params` included.
 * A guard over foreign input that checks only `code` while declaring `params`
 * non-optional hands `undefined` to the renderer, which fails with the worst
 * polarity available: invisible in English, crashing only once a translation is
 * loaded.
 */
export function hasMessageIdentity(data: unknown): data is HydraniumMessageData {
   if (typeof data !== 'object' || data === null || Array.isArray(data) || !('hydranium' in data)) {
      return false;
   }
   const identity = (data as { hydranium: unknown }).hydranium;
   if (typeof identity !== 'object' || identity === null || Array.isArray(identity)) {
      return false;
   }
   const candidate = identity as Partial<MessageIdentity>;
   return typeof candidate.code === 'string' && typeof candidate.params === 'object' && candidate.params !== null;
}

/**
 * A type alias rather than a subclass. Only `code`, `message` and `data` cross
 * the wire, so a subclass buys nothing there: `instanceof` does not survive
 * reconstruction, and the subclass costs an `Object.setPrototypeOf` in every
 * constructor purely to undo what `ResponseError`'s own constructor does.
 */
export type HydraniumResponseError = ResponseError<HydraniumMessageData>;

/**
 * The numeric `code` and the message's catalogue code are unrelated and both are
 * needed: `ResponseError.code` is an `integer`, so it cannot hold a
 * `hydranium/…` key, and it is what a caller switches on after reconstruction.
 */
export function messageError<S extends string>(
   code: number,
   message: MessageDefinition<S>,
   ...params: ParamsArg<S>
): HydraniumResponseError {
   return new ResponseError(code, message.format(...params), messageData(message, ...params));
}

/**
 * The reading user's language, as the tag a client declared.
 *
 * Neither validated nor normalised anywhere: rejecting an unfamiliar tag would
 * be selecting a locale, which the framework does not do.
 *
 * NOT a Langium language id, which names a grammar. The two are strings of the
 * same shape reachable from the same services, so one used where the other
 * belongs misses every lookup rather than failing.
 */
export type Locale = string;

/**
 * A host's templates for one locale, keyed by the whole message code.
 *
 * **Flat, not nested.** A code is one key here, `/` separators included; a host
 * whose own catalogue format nests — Theia's does — flattens on the way in. A
 * map that nests instead misses every lookup while type-checking, because the
 * value this is indexed by is `ResolvedMessage.code`.
 *
 * **Partial by nature rather than by convention.** A code with no entry renders
 * its English default, so a catalogue covering none of a package's codes is a
 * valid catalogue and not a broken one — which is what lets an adopter translate
 * as much or as little as they like.
 *
 * Deliberately NOT narrowed to the codes that exist, and a `Code extends string`
 * parameter would not buy what it appears to: a catalogue is loaded as JSON, so
 * it reaches a checked position as a variable rather than as an object literal,
 * and excess-property checking — the only thing that would reject a mistyped
 * key — does not run there. The gain is a compile error for a catalogue with no
 * correct key at all; the cost is a code union to maintain by hand, since a
 * declaration does not carry its code as a literal type.
 */
export type MessageCatalogue = Readonly<Record<string, string>>;

export namespace MessageCatalogue {
   /**
    * One catalogue from several, for a renderer whose override supplies entries
    * over the ones it inherits.
    *
    * **Later wins**, so a caller passes the inherited answer before its own.
    *
    * **No source at all answers `undefined`, not `{}`.** A renderer holds that
    * answer as the locale having no catalogue and renders straight through; an
    * empty object is a catalogue that misses every lookup, so returning one
    * turns the opt-out into a per-message search that yields the same English.
    */
   export function merge(...catalogues: ReadonlyArray<MessageCatalogue | undefined>): MessageCatalogue | undefined {
      const present = catalogues.filter((catalogue): catalogue is MessageCatalogue => catalogue !== undefined);
      if (present.length <= 1) {
         return present[0];
      }
      return Object.assign({}, ...present) as MessageCatalogue;
   }
}

/**
 * Render on the side that knows the reading user's locale. Omitting
 * `translations` is how an adopter without i18n opts out, and yields the
 * English.
 */
export function renderFrameworkMessage(message: ResolvedMessage, translations?: MessageCatalogue): string {
   const template = translations?.[message.code];
   return template ? interpolate(template, message.params) : message.text;
}

export function resolvedFromResponseError(error: ResponseError<unknown>): ResolvedMessage | undefined {
   return hasMessageIdentity(error.data) ? { ...error.data.hydranium, text: error.message } : undefined;
}

/**
 * The detail half of a `{detail}` placeholder. A technical error string is safe
 * to pass as a parameter for the same reason a number is: it is not itself
 * translatable text, so it needs no code of its own. A PROSE fragment is not,
 * and must become one code per value instead.
 *
 * For a reader of the client; the log takes {@link describeErrorWithStack}.
 */
export function describeError(error: unknown): string {
   return describeSafely(error, () => (error instanceof Error ? error.message : error));
}

/**
 * An error for the log: its stack where it has one, which names where it was
 * thrown, and its causes, which name what failed when an error wraps another.
 * Never for the client, whose reader cannot act on a stack.
 */
export function describeErrorWithStack(error: unknown): string {
   // A cause chain may loop back on itself.
   return describeChain(error, new Set());
}

function describeChain(error: unknown, seen: Set<unknown>): string {
   const lines: string[] = [];
   let current: unknown = error;
   let prefix = '';
   try {
      do {
         seen.add(current);
         const described = current;
         lines.push(prefix + describeSafely(described, () => (described instanceof Error ? stackOrMessage(described) : described)));
         // Node fails a connect to several addresses with one, whose own message is empty.
         if (current instanceof AggregateError) {
            try {
               const errors: unknown[] = current.errors;
               lines.push(...errors.filter(inner => !seen.has(inner)).map(inner => `Including: ${describeChain(inner, seen)}`));
            } catch {
               // An `errors` that is no list, or throws when read; the cause still follows.
            }
         }
         current = current instanceof Error ? current.cause : undefined;
         prefix = 'Caused by: ';
      } while (current !== undefined && current !== null && !seen.has(current));
   } catch {
      // A getter or proxy trap threw while walking the chain; keep what was described.
   }
   return lines.join('\n');
}

/** `error`'s stack as a string, or its message where reading or converting the stack throws. */
function stackOrMessage(error: Error): string {
   try {
      return String(error.stack ?? error.message);
   } catch {
      return String(error.message);
   }
}

/**
 * What `select()` picks from `value`, as a string, or where selecting or
 * converting it throws, as `String` does for an object without a prototype,
 * the `[object Type]` tag, or where reading the tag throws too, the value's
 * type. Describing an error must not throw, or the failure it reports is lost;
 * the conversion sits inside the guard because a thrown value's `message` or
 * `stack` need not be a string.
 */
function describeSafely(value: unknown, select: () => unknown): string {
   try {
      return String(select());
   } catch {
      try {
         return Object.prototype.toString.call(value);
      } catch {
         return `[${typeof value}]`;
      }
   }
}

/**
 * Recognises a declaration among a barrel's exports. The `format` check is what
 * discriminates: a `code` + `text` pair alone admits any object that happens to
 * carry both.
 */
export function isMessageDeclaration(value: unknown): value is MessageDefinition<string> {
   const candidate = value as { code?: unknown; text?: unknown; format?: unknown } | null;
   return (
      typeof candidate === 'object' &&
      candidate !== null &&
      typeof candidate.code === 'string' &&
      typeof candidate.text === 'string' &&
      typeof candidate.format === 'function'
   );
}

/**
 * Every declaration a `./messages` barrel exports.
 *
 * A caller cannot get there with `Object.values(barrel).filter(isMessageDeclaration)`:
 * a barrel's value type is a union of its declarations AND its functions, and
 * `filter` will not narrow a function type down to a `MessageDefinition`, so the
 * result stays the union and reading `.code` off it does not compile. Taking the
 * barrel as an opaque object is what makes the one-liner work.
 */
export function collectMessages(barrel: object): MessageDefinition<string>[] {
   return (Object.values(barrel) as unknown[]).filter(isMessageDeclaration);
}
